import React from "react";
import { styled } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import {
  Container,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  accordionSummaryClasses,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/**
 * PM5 T2 (money path, batch "freetrial-buynow") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * The Free Trial FAQ accordion. Styling-only conversion — the `faqs` array,
 * its copy and the `aria-controls`/`id` attributes are byte-identical.
 *
 * DEAD RULE DROPPED (guide §1.2 row 1): `accordionContainer` was declared in
 * the v4 `useStyles` object but never referenced from the JSX.
 */

// was: useStyles().background — a bare <div>, which has no `sx` prop.
const Background = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.ffNavy,
  padding: theme.spacing(6, 0),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(8, 0),
  },
}));

/**
 * ♦ v4's `<Grid container spacing={2}>` — ported verbatim rather than swapped
 * for v9's `gap` (guide §2, "Grid `spacing` implementation" + its T2/signin
 * refinement): `spacing` omitted (v9 default 0 ⇒ `gap: 0px`), the container
 * restates the negative margin and over-wide width, each item carries the 8px
 * padding. Identical by construction.
 */
const v4SpacingContainerSx = {
  width: "calc(100% + 16px)",
  margin: "-8px",
};
const v4GridItemSx = { padding: "8px" };

// was: useStyles().title
const titleSx = (theme: Theme) => ({
  letterSpacing: "-2px",
  fontSize: "2rem",
  lineHeight: "2.2rem",
  fontWeight: 600,
  textShadow: "-2px 2px 2px #171717",
  "& span": {
    color: "background.ffGreen",
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "3rem",
    lineHeight: "3.2rem",
  },
});

// was: useStyles().titleLarge
const titleLargeSx = (theme: Theme) => ({
  letterSpacing: "-2px",
  fontSize: "2.8rem",
  lineHeight: "2.8rem",
  fontWeight: 600,
  textShadow: "-2px 2px 2px #171717",
  [theme.breakpoints.up("sm")]: {
    fontSize: "5.4rem",
    lineHeight: "5rem",
  },
});

/**
 * was: useStyles().accordionEntry
 *
 * ♦ §1.2b HIT — the v4 rule reached into its child with the hard-coded global
 * selector `& .MuiAccordionSummary-expandIcon`. Two things move at once now
 * that this file converts: the `FfV5` class prefix, AND the slot itself, which
 * v5+ renamed `expandIcon` → `expandIconWrapper`. Re-prefixing alone would
 * match nothing. The name is read off `accordionSummaryClasses` so it tracks
 * both changes and fails loudly if the slot ever disappears (same shape as the
 * `Badge` / `Alert` rows in guide §2).
 *
 * MAPPING NOTE — v4 rendered the expand icon inside an `IconButton`
 * (`padding: 12`), which this rule overrode to `0px 12px`. v9 renders it inside
 * a plain `<div class="…-expandIconWrapper">` with no padding of its own, so
 * the same declaration produces the same box: icon + 12px either side, 0 top
 * and bottom. What does NOT carry over is the IconButton's circular hover/ripple
 * wash — that is a v5+ library-default change inherent to converting
 * `AccordionSummary`, not something this rule controlled, and re-creating it
 * would be a redesign rather than a port.
 */
const accordionEntrySx = (theme: Theme) => ({
  backgroundColor: "background.ffNavyLighter",
  borderRadius: "0 !important",
  padding: "4px 0px",
  marginBottom: "8px",
  [theme.breakpoints.up("sm")]: {
    padding: "8px 0px",
  },
  [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
    padding: "0px 12px",
    // PARITY RESTORE — v4's expand icon lived in an `<IconButton edge="end">`,
    // whose `edgeEnd` style declared `marginRight: -12`; v9's plain
    // `expandIconWrapper` div has no such rule, so the (unchanged, 48px-wide)
    // icon box occupies 48px of the summary row instead of 36px and the
    // question text loses 12px. Measured on the built /free-trial page at 390:
    // every FAQ question shrank 278.81px → 266.81px. Invisible at 1440, where
    // the text is shrink-to-fit and narrower than the space available.
    marginRight: "-12px",
  },
});

// was: useStyles().accordionEntryTitle
const accordionEntryTitleSx = (theme: Theme) => ({
  fontSize: "1.1rem",
  lineHeight: "1.2rem",
  fontWeight: 500,
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.3rem",
    lineHeight: "1.5rem",
  },
});

// was: useStyles().accordionEntryText
const accordionEntryTextSx = {
  fontSize: "1rem",
  lineHeight: "1.2rem",
  "& a": {
    color: "#eee",
  },
};

// was: useStyles().accordionEntryExpand
// `fill` has no entry in sx's `defaultSxConfig`, so a palette *path* string
// would be emitted raw as the CSS value (guide §1.2 row 4). Read off the theme.
const accordionEntryExpandSx = (theme: Theme) => ({
  fill: theme.palette.background.ffGreen,
  fontSize: "2.2rem",
  padding: 0,
});

const FAQs = () => {
  const faqs = [
    {
      title: "How do I take a Free Trial?",
      body: "Simple! click the 'Start Trial Now' button, or alternatively, click 'Start Trial On App' to get you up and running on the App.",
    },
    {
      title: "How long does the Free Trial last?",
      body: "The Free Trial lasts for 72 Hours from activation.  There are no limits or ads, so fill your boots and watch as much content as you possibly can.",
    },
    {
      title: "Why do you need my phone number?",
      body: "To activate a Free Trial we require your phone number to verify that it's actually you (a real person).  ",
    },
    {
      title: "Do I need to add my Credit Card for a Free Trial?",
      body: "No Credit/Debit card details are needed to activate a Free Trial.",
    },
    {
      title: "Are there watch limits in the Free Trial?",
      body: "There are no watch limits during your Trial experience. We have changed the dashboard for the Trial to show you our member favourites, and all 300+ episodes are available through search.",
    },
    {
      title: "Can I purchase a membership after my Trial?",
      body: "Absolutely, you can purchase a Full FarmFLiX Membership anytime during or after your Trial.",
    },
  ];

  return (
    <>
      <Background>
        <Container maxWidth="md" fixed>
          <Grid container sx={v4SpacingContainerSx}>
            <Grid size={{ xs: 12 }} sx={v4GridItemSx}>
              <Typography variant="h2" sx={titleSx}>
                Free Trial
              </Typography>
              <Typography variant="h2" sx={titleLargeSx}>
                FAQ&apos;s
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }} sx={v4GridItemSx}>
              {faqs.map((q, i) => (
                <Accordion sx={accordionEntrySx} key={i}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={accordionEntryExpandSx} />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <Typography sx={accordionEntryTitleSx}>
                      {q.title}
                    </Typography>
                  </AccordionSummary>
                  {/* PARITY RESTORE (guide §2, "`AccordionDetails` lost
                      `display: flex`") — v4's root was `{ display: 'flex',
                      padding: spacing(1, 2, 2) }`; v9 dropped the `display`
                      declaration, so the panel became a block container and its
                      single `<Typography>` child stopped being a shrink-to-fit
                      flex item. Measured on the built /free-trial page at 1440:
                      the answer paragraphs went 835.38 / 695.55 / 444.72 /
                      652.31px wide to a uniform 880px. */}
                  <AccordionDetails sx={{ display: "flex" }}>
                    <Typography sx={accordionEntryTextSx}>{q.body}</Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Grid>
          </Grid>
        </Container>
      </Background>
    </>
  );
};

export default FAQs;
